import { Component } from 'preact';

import isEqual from 'lodash.isequal';
import isNil from 'lodash.isnil';
import isNull from 'lodash.isnull';

const ANIMATION_TIMER = 200;
const KEY_UP = 38;
const KEY_DOWN = 40;

const TRANSLATION_AMOUNT = -100;

export default class PageScroller extends Component {

    static defaultProps = {
        animationTimer: 1000,
        transitionTimingFunction: "ease-in-out",
        containerHeight: typeof window === 'object' ? window.innerHeight : 0,
        containerWidth: "100%"
    };

    state = {
        componentIndex: 0,
        componentsToRender: [],
        containerHeight: typeof window === 'object' ? window.innerHeight : 0
    };

    previousTouchMove = null

    scrolling = false

    getPageClass = () => `page ${this.props.overflow ? 'overflow' : ''}`

    componentDidMount = () => {
        window.addEventListener('resize', this.onWindowResized);

        document.ontouchmove = (event) => {
            event.preventDefault();
        };

        this._pageContainer.addEventListener("wheel", this.wheelScroll);
        this._pageContainer.addEventListener("touchmove", this.touchMove);
        this._pageContainer.addEventListener("keydown", this.keyPress);

        const componentsToRender = [];

        if (!isNil(this.props.children[this.state.componentIndex])) {
            componentsToRender.push(
                <div key={this.state.componentIndex} ref={c => this["container_" + this.state.componentIndex] = c}
                     style={{height: "100%", width: "100%"}}
                     className={this.getPageClass()}
                >
                    {this.props.children[this.state.componentIndex]}
                </div>
            );
        } else {
            componentsToRender.push(
                <div ref={c => this["container_" + this.state.componentIndex] = c}
                     style={{height: "100%", width: "100%"}}
                     className={this.getPageClass()}
                >
                    {this.props.children}
                </div>
            );
        }

        this.addNextComponent(componentsToRender);

    };

    componentWillUnmount = () => {
        window.removeEventListener('resize', this.onWindowResized);

        this._pageContainer.removeEventListener("wheel", this.wheelScroll);
        this._pageContainer.removeEventListener("touchmove", this.touchMove);
        this._pageContainer.removeEventListener("keydown", this.keyPress);

    };

    goToPage = (number) => {
        const {pageOnChange, children} = this.props;
        const {componentIndex} = this.state;

        const componentsToRender = [...this.state.componentsToRender];

        if (!isEqual(componentIndex, number)) {
            if (!isNil(this["container_" + (number)]) && !this.scrolling) {

                this.scrolling = true;
                this._pageContainer.style.transform = `translate3d(0, ${(number) * TRANSLATION_AMOUNT}%, 0)`;

                if (pageOnChange) {
                    pageOnChange(number + 1);
                }

                if (isNil(this["container_" + (number + 1)]) && !isNil(children[number + 1]))
                    componentsToRender.push(
                        <div key={number + 1}
                             ref={c => this["container_" + (number + 1)] = c}
                             style={{height: "100%", width: "100%"}}
                             className={this.getPageClass()}
                        >
                            {children[number + 1]}
                        </div>
                    );

                setTimeout(() => {
                    this.setState({componentIndex: number, componentsToRender: componentsToRender}, () => {
                        this.scrolling = false;
                        this.previousTouchMove = null;
                    });
                }, this.props.animationTimer + ANIMATION_TIMER)

            } else if (!this.scrolling && !isNil(children[number])) {

                const componentsLength = componentsToRender.length;

                for (let i = componentsLength; i <= number; i++) {
                    componentsToRender.push(
                        <div key={i}
                             ref={c => this["container_" + i] = c}
                             style={{height: "100%", width: "100%"}}
                             className={this.getPageClass()}
                        >
                            {children[i]}
                        </div>
                    );
                }

                if (!isNil(children[number + 1])) {
                    componentsToRender.push(
                        <div key={number + 1}
                             ref={c => this["container_" + (number + 1)] = c}
                             style={{height: "100%", width: "100%"}}
                             className={this.getPageClass()}
                        >
                            {children[number + 1]}
                        </div>
                    );
                }

                this.scrolling = true;
                this.setState({
                    componentsToRender
                }, () => {
                    this._pageContainer.style.transform = `translate3d(0, ${(number) * TRANSLATION_AMOUNT}%, 0)`;

                    if (pageOnChange) {
                        pageOnChange(number + 1);
                    }

                    setTimeout(() => {
                        this.setState({componentIndex: number}, () => {
                            this.scrolling = false;
                            this.previousTouchMove = null;
                        });
                    }, this.props.animationTimer + ANIMATION_TIMER)
                });
            }
        }
    };

    render() {
        const {animationTimer, transitionTimingFunction, containerWidth} = this.props;
        const { containerHeight } = this.state;

        return (
            <div style={{height: containerHeight, width: containerWidth, overflow: "hidden"}} className="page-scroller">
                <div ref={c => this._pageContainer = c}
                     style={{
                         height: "100%",
                         width: "100%",
                         transition: `transform ${animationTimer}ms ${transitionTimingFunction}`
                     }}
                     tabIndex={0}>
                    {this.state.componentsToRender}
                </div>
            </div>
        )
    }

    wheelScroll = (event) => {
        if (event.deltaY < 0) {
            this.scrollWindowUp();
        } else {
            this.scrollWindowDown();
        }

    };

    touchMove = (event) => {
        if (!isNull(this.previousTouchMove)) {
            if (event.touches[0].clientY > this.previousTouchMove) {
                this.scrollWindowUp();
            } else {
                this.scrollWindowDown();
            }
        } else {
            this.previousTouchMove = event.touches[0].clientY;
        }
    };

    keyPress = (event) => {
        if (isEqual(event.keyCode, KEY_UP)) {
            this.scrollWindowUp();
        }
        if (isEqual(event.keyCode, KEY_DOWN)) {
            this.scrollWindowDown();
        }
    };

    onWindowResized = () => {
        this.setState({ containerHeight: window.innerHeight })
        this.forceUpdate();
    };

    addNextComponent = (onMountedComponents) => {
        let componentsToRender = [];

        if (!isNil(onMountedComponents)) {
            componentsToRender = [...onMountedComponents];
        }

        componentsToRender = [...componentsToRender, ...this.state.componentsToRender];

        if (!componentsToRender[this.state.componentIndex + 1]) {
            if (!isNil(this.props.children[this.state.componentIndex + 1])) {
                componentsToRender.push(
                    <div key={this.state.componentIndex + 1}
                         ref={c => this["container_" + (this.state.componentIndex + 1)] = c}
                         style={{height: "100%", width: "100%"}}
                         className={this.getPageClass()}
                    >
                        {this.props.children[this.state.componentIndex + 1]}
                    </div>
                );
            }
        }

        this.setState({componentsToRender: [...componentsToRender]});
    };

    scrollWindowUp = () => {
        if (!isNil(this["container_" + (this.state.componentIndex - 1)]) && !this.scrolling) {

            this.scrolling = true;
            this._pageContainer.style.transform = `translate3d(0, ${(this.state.componentIndex - 1) * TRANSLATION_AMOUNT}%, 0)`;

            if (this.props.pageOnChange) {
                this.props.pageOnChange(this.state.componentIndex);
            }

            setTimeout(() => {
                this.setState((prevState) => ({componentIndex: prevState.componentIndex - 1}), () => {
                    this.scrolling = false;
                    this.previousTouchMove = null;
                });
            }, this.props.animationTimer + ANIMATION_TIMER)

        } else if (this.props.scrollUnavailable) {
            this.props.scrollUnavailable();
        }
    };

    scrollWindowDown = () => {
        if (!isNil(this["container_" + (this.state.componentIndex + 1)]) && !this.scrolling) {

            this.scrolling = true;
            this._pageContainer.style.transform = `translate3d(0, ${(this.state.componentIndex + 1) * TRANSLATION_AMOUNT}%, 0)`;

            if (this.props.pageOnChange) {
                this.props.pageOnChange(this.state.componentIndex + 2);
            }

            setTimeout(() => {
                this.setState((prevState) => ({componentIndex: prevState.componentIndex + 1}), () => {
                    this.scrolling = false;
                    this.previousTouchMove = null;
                    this.addNextComponent();
                });
            }, this.props.animationTimer + ANIMATION_TIMER)

        } else if (this.props.scrollUnavailable) {
            this.props.scrollUnavailable();
        }
    };
}


// WEBPACK FOOTER //
// ./components/page-scroller/index.js